import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShoppingCart,
  MoreHorizontal,
  ArrowUpRight,
  MoreVertical,
  Check,
  Truck,
  X,
  FileText,
  IndianRupee,
  CreditCard,
  AlertCircle,
  ReceiptText,
  Printer,
  Mail,
  Phone,
  MapPin,
  Banknote,
  Wallet,
  Ban,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DetailField } from '@/components/common/DetailField';
import { DialogEyebrow, SectionHeading } from '@/components/common/DialogPrimitives';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import { printHtmlInWindow } from '@/lib/print';
import { ordersService } from '@/services/orders';
import { paymentsService } from '@/services/payments';
import { refundsService } from '@/services/refunds';
import { cancelsService } from '@/services/cancels';
import { returnsService } from '@/services/returns';
import { invoicesService } from '@/services/invoices';
import { dispatchService } from '@/services/dispatch';
import type { DispatchOrder, DispatchStatus } from '@/types/dispatch';
import type { SalesOrder, SalesOrderStatus } from '@/types/orders';
import type { Payment, PaymentStatus } from '@/types/payments';
import type { Invoice, InvoiceStatus } from '@/types/invoices';
import { PaymentInitiateDialog } from './PaymentInitiateDialog';
import { RefundInitiateDialog } from '../refunds/RefundInitiateDialog';
import { GenerateInvoiceDialog } from '../invoices/GenerateInvoiceDialog';
import {
  type OrderActionState,
  canGenerateInvoice as canGenerateInvoicePolicy,
  canMarkReadyForDispatch,
  canMarkDispatched as canMarkDispatchedPolicy,
} from './orderActions';

interface Props {
  order: SalesOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABEL: Record<SalesOrderStatus, string> = {
  DRAFT: 'Draft',
  CREATED: 'Order placed',
  STOCK_RESERVED: 'Stock reserved',
  PAYMENT_PENDING: 'Payment pending',
  PAYMENT_CONFIRMED: 'Payment confirmed',
  READY_FOR_DISPATCH: 'Ready to dispatch',
  DISPATCHED: 'Dispatched',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<SalesOrderStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'muted',
  CREATED: 'muted',
  STOCK_RESERVED: 'muted',
  PAYMENT_PENDING: 'warning',
  PAYMENT_CONFIRMED: 'success',
  READY_FOR_DISPATCH: 'success',
  DISPATCHED: 'success',
  DELIVERED: 'success',
  CANCELLED: 'destructive',
};

export function OrderDetailSheet({ order, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [pendingStatus, setPendingStatus] = useState<SalesOrderStatus | null>(null);
  const [takePaymentOpen, setTakePaymentOpen] = useState(false);
  const [issueRefundOpen, setIssueRefundOpen] = useState(false);
  const [generateInvoiceOpen, setGenerateInvoiceOpen] = useState(false);
  const [cancellingInvoice, setCancellingInvoice] = useState<Invoice | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // Re-fetch the canonical order detail when the dialog opens — the list
  // payload has the same shape but staleness from list-cache can confuse
  // the status-machine readout.
  const detailQuery = useQuery({
    queryKey: ['orders', 'detail', order?.id],
    queryFn: () => ordersService.get(order!.id),
    enabled: !!order && open,
    initialData: order ?? undefined,
    staleTime: 5_000,
  });

  const current = detailQuery.data ?? order;

  // Order-scoped payments — drives the Payments tab and the header chip
  // that surfaces "captured / outstanding" at a glance.
  const paymentsQuery = useQuery({
    queryKey: ['payments', 'forOrder', current?.id],
    queryFn: () => paymentsService.list({ salesOrderId: current!.id, pageSize: 50 }),
    enabled: !!current && open,
  });

  const payments = paymentsQuery.data?.rows ?? [];
  const successPayments = payments.filter((p) => p.status === 'SUCCESS');
  const captured = successPayments.reduce((s, p) => s + Number(p.amount), 0);

  // Order-scoped refunds — drives the Refunds tab and lets us subtract
  // already-refunded amounts when surfacing "captured net of refunds".
  const refundsQuery = useQuery({
    queryKey: ['refunds', 'forOrder', current?.id],
    queryFn: () => refundsService.listForOrder(current!.id),
    enabled: !!current && open,
  });
  const refunds = refundsQuery.data ?? [];

  // Order-scoped aftersales — cancel requests + returns against this
  // SO. Answers "where do I see aftersales for this particular order?"
  // without forcing the operator to filter the Cancel Requests /
  // Return Requests pages by salesOrderId.
  const cancelsForOrderQuery = useQuery({
    queryKey: ['cancels', 'forOrder', current?.id],
    queryFn: () => cancelsService.list({ salesOrderId: current!.id, pageSize: 50 }),
    enabled: !!current && open,
  });
  const returnsForOrderQuery = useQuery({
    queryKey: ['returns', 'forOrder', current?.id],
    queryFn: () => returnsService.list({ salesOrderId: current!.id, pageSize: 50 }),
    enabled: !!current && open,
  });
  const cancelsForOrder = cancelsForOrderQuery.data?.rows ?? [];
  const returnsForOrder = returnsForOrderQuery.data?.rows ?? [];
  const refundedTotal = refunds
    .filter((r) => r.status === 'PROCESSED' || r.status === 'PARTIAL_REFUND')
    .reduce((s, r) => s + Number(r.amount), 0);

  // Order-scoped invoices — drives the Invoices tab + the Generate-invoice
  // CTA gating (we hide the button when an active invoice already exists).
  const invoicesQuery = useQuery({
    queryKey: ['invoices', 'forOrder', current?.id],
    queryFn: () => invoicesService.listForOrder(current!.id),
    enabled: !!current && open,
  });
  const invoices = invoicesQuery.data ?? [];
  const activeInvoice = invoices.find(
    (i) => i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID' || i.status === 'PAID',
  );

  // Order-scoped dispatches. The forward dispatch (warehouse → customer)
  // drives the SO status automatically: when it is picked the SO flips
  // to DISPATCHED, when delivered the SO flips to DELIVERED. So once a
  // forward dispatch exists we suppress the manual SO-status buttons —
  // marking the SO by hand would desync it from the real shipment.
  const dispatchesForOrderQuery = useQuery({
    queryKey: ['dispatches', 'forOrder', current?.id],
    queryFn: () => dispatchService.list({ salesOrderId: current!.id, pageSize: 50 }),
    enabled: !!current && open,
  });
  const dispatches = dispatchesForOrderQuery.data?.rows ?? [];
  const forwardDispatch = dispatches.find((d) => d.direction === 'FORWARD');

  const printInvoice = useMutation({
    mutationFn: (id: string) => invoicesService.print(id),
    onSuccess: (printable) => {
      const opened = printHtmlInWindow(printable.html, { width: 900, height: 1100 });
      if (!opened) {
        toast.error('Popup blocked. Allow popups to print invoices.');
        return;
      }
      toast.success('Invoice rendered');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not render invoice'),
  });

  // Invoice cancellation — required to re-generate when the issued
  // invoice has a mistake. BE's cancel endpoint flips status →
  // CANCELLED; afterwards `activeInvoice` resolves null again and
  // the Generate-invoice CTA reappears in the section header.
  const cancelInvoice = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      invoicesService.cancel(id, reason || undefined),
    onSuccess: (saved, vars) => {
      toast.success(`Invoice ${saved.invoiceNumber} cancelled — generate a new one to replace it.`);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'forOrder', current?.id] });
      setCancellingInvoice(null);
      setCancelReason('');
      // For the common case ("I need to re-issue"), open the Generate
      // dialog automatically so the operator doesn't have to hunt for
      // the CTA — matches the "Regenerate invoice" mental model.
      void vars;
      setGenerateInvoiceOpen(true);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not cancel invoice'),
  });

  const mutation = useMutation({
    mutationFn: (status: SalesOrderStatus) =>
      ordersService.updateStatus(current!.id, { status }),
    onSuccess: (_data, status) => {
      toast.success(`Order moved to ${STATUS_LABEL[status]}`);
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setPendingStatus(null);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Could not update status');
      setPendingStatus(null);
    },
  });

  const update = (next: SalesOrderStatus) => {
    setPendingStatus(next);
    mutation.mutate(next);
  };

  const status = current?.status ?? 'CREATED';
  // Shared order-action policy — same gates the orders grid uses, so the
  // two surfaces never disagree. `hasInvoice` uses the live per-order
  // invoice query (authoritative right after generate/cancel).
  const actionState: OrderActionState | null = current
    ? {
        status: current.status,
        paymentMode: current.paymentMode,
        paymentStatus: current.paymentStatus,
        hasInvoice: !!activeInvoice,
        dispatchStatus: current.fulfilment?.status ?? null,
        hasTracking: !!current.fulfilment?.trackingNumber,
      }
    : null;
  const canMarkReady = !!actionState && canMarkReadyForDispatch(actionState);
  const canMarkDispatched = !!actionState && canMarkDispatchedPolicy(actionState);
  const canCancel = !['DISPATCHED', 'DELIVERED', 'CANCELLED'].includes(status);
  const isTerminal = ['DISPATCHED', 'DELIVERED', 'CANCELLED'].includes(status);

  // Render every money field on this sheet in the order's currency.
  // Payments and invoices for an order are always in the order's
  // currency (the BE pins both on create), so they ride along.
  const orderCurrency = current?.currency ?? 'INR';
  const money = (value: number | null | undefined) => fmt.money(value, orderCurrency);

  // Totals — recompute from items if BE didn't materialise totalAmount.
  const lineSubtotal = (current?.items ?? []).reduce((s, l) => {
    const lt = l.lineTotal != null ? Number(l.lineTotal) : null;
    if (lt != null) return s + lt;
    if (l.unitPrice != null) return s + Number(l.unitPrice) * Number(l.quantity);
    return s;
  }, 0);
  const subtotal = current?.totalAmount != null ? Number(current.totalAmount) : lineSubtotal;

  // Tax — prefer the invoice's locked values once issued. Before that,
  // surface the order's `taxPreview` so the operator can verify the
  // breakdown before clicking Generate invoice. Falls back to null only
  // when there's no preview (regime unsupported, missing address, etc.).
  const taxPreview =
    current?.taxPreview && current.taxPreview.available ? current.taxPreview : null;
  const taxUnavailableReason =
    current?.taxPreview && !current.taxPreview.available
      ? current.taxPreview.reason
      : null;
  const taxTotal = activeInvoice
    ? Number(activeInvoice.totalCgst) +
      Number(activeInvoice.totalSgst) +
      Number(activeInvoice.totalIgst)
    : taxPreview
      ? Number(taxPreview.totalTax)
      : null;
  const previewCgst = taxPreview ? Number(taxPreview.totalCgst) : 0;
  const previewSgst = taxPreview ? Number(taxPreview.totalSgst) : 0;
  const previewIgst = taxPreview ? Number(taxPreview.totalIgst) : 0;
  // M-money-model — read the order's own breakdown fields. These are
  // present from create (or via PATCH /orders/:id/totals later). Tax
  // preview comes from the same TaxStrategy invoice generation uses, so
  // the order detail can show the full customer-pays figure before the
  // operator commits to issuing an invoice.
  const orderShipping = current?.shippingCharge != null ? Number(current.shippingCharge) : 0;
  const orderDiscount = current?.discountAmount != null ? Number(current.discountAmount) : 0;
  const orderRoundOff = current?.roundOff != null ? Number(current.roundOff) : 0;
  const orderAdditional = current?.additionalCharges ?? [];
  const orderAdditionalSum = orderAdditional.reduce(
    (s, r) => s + (Number.isFinite(Number(r.amount)) ? Number(r.amount) : 0),
    0,
  );
  // Grand total preference: invoice (tax-inclusive, post-issue) → order
  // (M-money-model breakdown, pre-tax) → subtotal (legacy). Falls back
  // to subtotal for pre-migration orders that never had a grandTotal
  // stamped (the BE migration backfilled grandTotal = totalAmount).
  const invoiceGrandTotal = activeInvoice ? Number(activeInvoice.grandTotal) : null;
  const orderGrandTotalRaw =
    current?.grandTotal != null
      ? Number(current.grandTotal)
      : subtotal + orderShipping + orderAdditionalSum - orderDiscount + orderRoundOff;
  // Customer-pays figure — already gross when stamp is INCLUSIVE,
  // adds tax on top when EXCLUSIVE. Falls back to the raw grandTotal
  // (legacy / no priced lines / no preview).
  const orderCustomerPays =
    taxPreview?.customerPaysTotal != null
      ? Number(taxPreview.customerPaysTotal)
      : orderGrandTotalRaw + (taxTotal ?? 0);
  const grandTotal = invoiceGrandTotal ?? orderCustomerPays;
  const isInclusive = current?.taxPricingMode === 'INCLUSIVE';
  // "Billable" = what the customer is actually on the hook for. Outstanding
  // tracks against this so operators don't under-collect on COD orders
  // that already have an invoice with tax rolled in.
  const billable = grandTotal;
  const outstanding = Math.max(0, billable - captured);
  const canTakePayment =
    !!current &&
    !['DELIVERED', 'CANCELLED'].includes(status) &&
    outstanding > 0;
  // Refunds are a post-fulfilment / cancellation action — surfacing the
  // CTA mid-pack confused operators ("should I refund this?" during
  // active dispatch). Gate by terminal state. Edge cases (refund an
  // overpayment before delivery) can still be issued via the Refunds
  // surface or by cancelling first.
  const canIssueRefund =
    !!current &&
    captured > 0 &&
    refundedTotal < captured &&
    ['DELIVERED', 'CANCELLED'].includes(status);
  const canGenerateInvoice = !!actionState && canGenerateInvoicePolicy(actionState);

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[88vh] w-full max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-7 pt-6 pb-5">
            <DialogTitle className="sr-only">{current?.orderNumber ?? 'Order detail'}</DialogTitle>
            {current && (
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
                >
                  <ShoppingCart className="h-6 w-6" />
                </span>

                <div className="min-w-0 flex-1 space-y-1.5">
                  <DialogEyebrow>
                    {current.source === 'PORTAL' ? 'Portal order' : 'Internal order'} ·{' '}
                    {fmt.relative(current.createdAt)}
                  </DialogEyebrow>
                  <h2 className="font-mono text-[18px] font-semibold leading-tight tracking-tight text-foreground">
                    {current.orderNumber}
                  </h2>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={STATUS_TONE[status]} className="text-[10px]">
                      {STATUS_LABEL[status]}
                    </Badge>
                    {/* Live shipment status alongside the coarse order status —
                        surfaces real progress (In transit, Out for delivery)
                        and a FAILED courier booking the order status hides. */}
                    {current.fulfilment && (
                      <Badge
                        variant={DISPATCH_STATUS_TONE[current.fulfilment.status]}
                        className="text-[10px]"
                        title={
                          current.fulfilment.status === 'FAILED'
                            ? 'Courier booking failed — open the dispatch to retry'
                            : `Live shipment status · dispatch ${current.fulfilment.dispatchNumber}`
                        }
                      >
                        <Truck className="h-3 w-3" />
                        {DISPATCH_STATUS_LABEL[current.fulfilment.status]}
                      </Badge>
                    )}
                    <Badge
                      variant={current.paymentMode === 'COD' ? 'warning' : 'muted'}
                      className="text-[10px]"
                      title={
                        current.paymentMode === 'COD'
                          ? 'Cash on delivery — collected by courier on delivery'
                          : 'Prepaid — payment confirmed before dispatch'
                      }
                    >
                      {current.paymentMode === 'COD' ? (
                        <Banknote className="h-3 w-3" />
                      ) : (
                        <Wallet className="h-3 w-3" />
                      )}
                      {current.paymentMode ?? 'PREPAID'}
                    </Badge>
                    {current.paymentMode !== 'COD' && current.paymentStatus && (
                      <Badge
                        variant={
                          current.paymentStatus === 'PAID'
                            ? 'success'
                            : current.paymentStatus === 'PARTIAL'
                              ? 'warning'
                              : 'destructive'
                        }
                        className="text-[10px]"
                        title={`Captured payments vs order total — ${money(captured)} of ${money(Number(current.grandTotal ?? 0))}`}
                      >
                        {current.paymentStatus === 'PAID'
                          ? 'Paid'
                          : current.paymentStatus === 'PARTIAL'
                            ? 'Part-paid'
                            : 'Unpaid'}
                      </Badge>
                    )}
                    <Badge variant="muted" className="text-[10px]">
                      {current.customer.name}
                    </Badge>
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {current.customer.code}
                    </Badge>
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {current.warehouse.code}
                    </Badge>
                  </div>
                </div>

                {canTakePayment && (
                  <Button
                    size="sm"
                    onClick={() => setTakePaymentOpen(true)}
                    className="shrink-0"
                  >
                    <CreditCard className="h-3.5 w-3.5" />
                    Take payment
                  </Button>
                )}
                {canIssueRefund && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIssueRefundOpen(true)}
                    className="shrink-0"
                  >
                    <IndianRupee className="h-3.5 w-3.5" />
                    Issue refund
                  </Button>
                )}
                {canGenerateInvoice && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setGenerateInvoiceOpen(true)}
                    className="shrink-0"
                  >
                    <ReceiptText className="h-3.5 w-3.5" />
                    Generate invoice
                  </Button>
                )}
                {activeInvoice && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => printInvoice.mutate(activeInvoice.id)}
                    disabled={printInvoice.isPending}
                    className="shrink-0"
                  >
                    <Printer className="h-3.5 w-3.5" />
                    Print invoice
                  </Button>
                )}
              </div>
            )}
          </DialogHeader>
          <Separator />

          {/* Stats strip */}
          <div className="flex items-stretch divide-x divide-border/60 border-b border-border/60 bg-muted/20 px-7">
            <Stat label="Subtotal (pre-tax)" value={money(subtotal)} accent />
            <Stat
              label="Tax"
              value={taxTotal != null ? money(taxTotal) : 'At invoice'}
              muted={taxTotal == null}
            />
            <Stat
              label={
                invoiceGrandTotal != null
                  ? 'Grand total (invoice)'
                  : taxPreview
                    ? isInclusive
                      ? 'Grand total (tax incl.)'
                      : 'Grand total (with tax)'
                    : isInclusive
                      ? 'Grand total (tax incl.)'
                      : 'Grand total (pre-tax)'
              }
              value={money(billable)}
              accent
            />
            <Stat label="Captured" value={money(captured)} />
            <Stat
              label="Outstanding"
              value={money(outstanding)}
              accent={outstanding > 0}
              muted={outstanding === 0}
            />
            <Stat label="Lines" value={fmt.number(current?.items.length ?? 0)} muted />
          </div>

          <ScrollArea className="flex-1">
            <div className="space-y-6 px-7 py-5">
              {/* ─── Top: two-column dashboard ─────────────────────── */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* LEFT: Customer + Ship-to */}
                <section className="space-y-5">
                  <div className="space-y-3">
                    <SectionHeading title="Customer" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <DetailField label="Name" value={current?.customer.name} />
                      <DetailField
                        label="Code"
                        value={<span className="font-mono">{current?.customer.code}</span>}
                      />
                      <DetailField
                        label="Email"
                        value={
                          current?.customer.email ? (
                            <a
                              href={`mailto:${current.customer.email}`}
                              className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline"
                            >
                              <Mail className="h-3 w-3 text-muted-foreground" />
                              {current.customer.email}
                            </a>
                          ) : (
                            '—'
                          )
                        }
                      />
                      <DetailField
                        label="Phone"
                        value={
                          current?.customer.phone ? (
                            <a
                              href={`tel:${current.customer.phone}`}
                              className="inline-flex items-center gap-1 font-mono text-foreground underline-offset-2 hover:underline"
                            >
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {current.customer.phone}
                            </a>
                          ) : (
                            '—'
                          )
                        }
                      />
                      <DetailField
                        label="GSTIN"
                        value={
                          current?.customer.gstin ? (
                            <span className="font-mono">{current.customer.gstin}</span>
                          ) : (
                            '—'
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <SectionHeading
                      title="Ship to"
                      hint={current?.source === 'PORTAL' ? 'Captured at portal checkout' : undefined}
                    />
                    {current?.customer.shippingAddress ? (
                      <ShippingAddressBlock address={current.customer.shippingAddress} />
                    ) : (
                      <div className="rounded-lg border border-dashed border-warning/40 bg-warning/5 px-4 py-3 text-[12.5px] text-[hsl(38_85%_30%)]">
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" />
                          No shipping address on file for this customer. Dispatch will block
                          until one is added.
                        </span>
                      </div>
                    )}
                  </div>
                </section>

                {/* RIGHT: Fulfilment + Money breakdown + Notes */}
                <section className="space-y-5">
                  <div className="space-y-3">
                    <SectionHeading title="Fulfilment" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <DetailField label="Warehouse" value={current?.warehouse.name} />
                      <DetailField
                        label="Warehouse code"
                        value={<span className="font-mono">{current?.warehouse.code}</span>}
                      />
                      <DetailField
                        label="Source"
                        value={current?.source === 'PORTAL' ? 'Customer portal' : 'Internal booking'}
                      />
                      <DetailField
                        label="Payment mode"
                        value={
                          <span className="inline-flex items-center gap-1">
                            {current?.paymentMode === 'COD' ? (
                              <Banknote className="h-3.5 w-3.5 text-muted-foreground" />
                            ) : (
                              <Wallet className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                            {current?.paymentMode === 'COD' ? 'Cash on delivery' : 'Prepaid'}
                          </span>
                        }
                      />
                      <DetailField
                        label="Currency"
                        value={<span className="font-mono">{orderCurrency}</span>}
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <SectionHeading
                      title="Money breakdown"
                      hint={
                        isInclusive
                          ? 'Prices are tax-inclusive (MRP)'
                          : 'Prices are tax-exclusive'
                      }
                    />
                    <div className="space-y-1.5 rounded-md bg-muted/40 p-4 text-[13px]">
                      <Row
                        label={isInclusive ? 'Subtotal (incl. tax)' : 'Subtotal (pre-tax)'}
                        value={money(subtotal)}
                      />
                      {activeInvoice ? (
                        // Invoice issued — show its locked CGST/SGST/IGST.
                        Number(activeInvoice.totalIgst) > 0 ? (
                          <Row
                            label="IGST (inter-state)"
                            value={money(Number(activeInvoice.totalIgst))}
                            muted
                          />
                        ) : (
                          <>
                            <Row
                              label="CGST"
                              value={money(Number(activeInvoice.totalCgst))}
                              muted
                            />
                            <Row
                              label="SGST"
                              value={money(Number(activeInvoice.totalSgst))}
                              muted
                            />
                          </>
                        )
                      ) : taxPreview ? (
                        // Pre-invoice preview from the same TaxStrategy.
                        // INCLUSIVE: tax is "embedded" in subtotal — show as informational
                        // ("of which tax: ₹X"). EXCLUSIVE: shown as a separate add-on.
                        previewIgst > 0 ? (
                          <Row
                            label={
                              isInclusive
                                ? 'IGST (inter-state) · of which'
                                : 'IGST (inter-state) · preview'
                            }
                            value={money(previewIgst)}
                            muted
                          />
                        ) : previewCgst > 0 || previewSgst > 0 ? (
                          <>
                            <Row
                              label={isInclusive ? 'CGST · of which' : 'CGST · preview'}
                              value={money(previewCgst)}
                              muted
                            />
                            <Row
                              label={isInclusive ? 'SGST · of which' : 'SGST · preview'}
                              value={money(previewSgst)}
                              muted
                            />
                          </>
                        ) : (
                          <Row label="Tax" value={money(0)} muted />
                        )
                      ) : taxUnavailableReason ? (
                        <Row label="Tax" value={taxUnavailableReason} muted />
                      ) : (
                        <Row label="Tax" value="—" muted />
                      )}
                      {orderShipping > 0 && (
                        <Row label="Shipping charge" value={money(orderShipping)} />
                      )}
                      {current?.selectedServiceTier && (
                        <Row
                          label="Delivery plan"
                          value={
                            current.selectedServiceTier === 'EXPRESS'
                              ? 'Express'
                              : current.selectedServiceTier === 'STANDARD'
                                ? 'Standard'
                                : current.selectedServiceTier
                          }
                        />
                      )}
                      {current?.committedEdd && (
                        <Row label="Promised delivery" value={fmt.date(current.committedEdd)} />
                      )}
                      {orderAdditional.map((row, idx) => (
                        <Row
                          key={`${row.label}-${idx}`}
                          label={row.label || `Charge ${idx + 1}`}
                          value={money(Number(row.amount))}
                        />
                      ))}
                      {orderDiscount > 0 && (
                        <Row label="Discount" value={`− ${money(orderDiscount)}`} />
                      )}
                      {orderRoundOff !== 0 && (
                        <Row
                          label="Round off"
                          value={`${orderRoundOff >= 0 ? '+' : '−'} ${money(Math.abs(orderRoundOff))}`}
                        />
                      )}
                      {orderShipping === 0 &&
                        orderAdditional.length === 0 &&
                        orderDiscount === 0 &&
                        orderRoundOff === 0 && (
                          <Row label="Shipping / discount / charges" value="—" muted />
                        )}
                      <Separator className="my-1.5" />
                      <Row
                        label={
                          invoiceGrandTotal != null
                            ? 'Grand total (invoice)'
                            : isInclusive
                              ? 'Grand total · customer pays'
                              : taxPreview
                                ? 'Grand total (with tax) · preview'
                                : 'Grand total (pre-tax)'
                        }
                        value={money(billable)}
                        bold
                      />
                      {captured > 0 && (
                        <Row label="Captured" value={money(captured)} muted />
                      )}
                      {outstanding > 0 && (
                        <Row
                          label={current?.paymentMode === 'COD' ? 'COD due on delivery' : 'Outstanding'}
                          value={money(outstanding)}
                          muted
                        />
                      )}
                    </div>
                    {invoiceGrandTotal == null && taxPreview && (
                      <div className="rounded-md border border-info/20 bg-info/5 px-3 py-2 text-[11.5px] text-muted-foreground">
                        {isInclusive
                          ? 'Tax is included in the unit prices (MRP). '
                          : 'Tax preview based on '}
                        {taxPreview.isIntraState === true
                          ? 'intra-state'
                          : taxPreview.isIntraState === false
                            ? 'inter-state'
                            : 'current'}{' '}
                        ship-to ({taxPreview.placeOfSupply ?? '—'} → {taxPreview.billingState ?? '—'}).
                        Click <strong>Generate invoice</strong> when verified — the invoice
                        locks these numbers and assigns the invoice number.
                      </div>
                    )}
                    {invoiceGrandTotal == null && !taxPreview && taxUnavailableReason && (
                      <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
                        Tax preview unavailable — {taxUnavailableReason}
                      </div>
                    )}
                  </div>

                  {current?.notes && (
                    <div className="space-y-2">
                      <SectionHeading title="Notes" />
                      <p className="text-[13px] leading-relaxed text-foreground">{current.notes}</p>
                    </div>
                  )}
                </section>
              </div>

              <Separator />

              {/* ─── Line items (full width) ───────────────────────── */}
              <section className="space-y-3">
                <SectionHeading
                  title="Line items"
                  hint={`${current?.items.length ?? 0} line${
                    current?.items.length === 1 ? '' : 's'
                  } · ${fmt.number(
                    (current?.items ?? []).reduce((s, l) => s + Number(l.quantity), 0),
                  )} units`}
                />
                {!current || current.items.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border/70 px-4 py-10 text-center text-[12.5px] text-muted-foreground">
                    No line items recorded.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-border/70">
                    <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      <div className="col-span-5">Item</div>
                      <div className="col-span-2 text-right">Qty</div>
                      <div className="col-span-2 text-right">Unit</div>
                      <div className="col-span-3 text-right">Line total</div>
                    </div>
                    {current.items.map((l) => (
                      <div
                        key={l.id}
                        className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
                      >
                        <div className="col-span-5 min-w-0">
                          <div className="truncate font-medium text-foreground">{l.name}</div>
                          <div className="font-mono text-[10.5px] text-muted-foreground">
                            {l.sku}
                          </div>
                        </div>
                        <div className="col-span-2 text-right font-mono tabular-nums">
                          {fmt.number(Number(l.quantity))}
                        </div>
                        <div className="col-span-2 text-right font-mono tabular-nums text-muted-foreground">
                          {l.unitPrice != null ? money(Number(l.unitPrice)) : '—'}
                        </div>
                        <div className="col-span-3 text-right font-mono tabular-nums text-foreground">
                          {l.lineTotal != null ? money(Number(l.lineTotal)) : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <Separator />

              {/* ─── Dispatch (collapsible) ────────────────────────── */}
              <CollapsibleSection
                title="Dispatch"
                count={dispatches.length}
                defaultOpen={dispatches.length > 0}
              >
                {dispatches.length === 0 && !dispatchesForOrderQuery.isLoading ? (
                  <div className="rounded-lg border border-dashed border-border/70 px-4 py-8 text-center">
                    <Truck className="mx-auto h-5 w-5 text-muted-foreground/50" />
                    <div className="mt-2 text-[12.5px] text-muted-foreground">
                      No dispatch created yet. A dispatch is materialised once
                      the order is ready for fulfilment.
                    </div>
                  </div>
                ) : dispatches.length > 0 ? (
                  <div className="space-y-2">
                    {dispatches.map((d) => (
                      <DispatchRow key={d.id} dispatch={d} />
                    ))}
                    {forwardDispatch && (
                      <p className="px-1 text-[11.5px] text-muted-foreground">
                        This order&apos;s status follows the dispatch — it flips
                        to Dispatched when the shipment is picked, and to
                        Delivered on delivery. No manual step needed.
                      </p>
                    )}
                  </div>
                ) : null}
              </CollapsibleSection>

              {/* ─── Payments (collapsible) ────────────────────────── */}
              <CollapsibleSection
                title="Payments"
                count={payments.length}
                defaultOpen={payments.length > 0 || outstanding > 0}
                action={
                  canTakePayment ? (
                    <Button size="sm" variant="outline" onClick={() => setTakePaymentOpen(true)}>
                      <CreditCard className="h-3.5 w-3.5" />
                      Take payment
                    </Button>
                  ) : null
                }
              >
                {outstanding > 0 && payments.length > 0 && (
                  <div className="mb-3 flex items-center gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[12px] text-[hsl(38_85%_30%)]">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      <span className="font-semibold">{money(outstanding)}</span> still
                      outstanding on this order.
                    </span>
                  </div>
                )}
                {payments.length === 0 && !paymentsQuery.isLoading ? (
                  <div className="rounded-lg border border-dashed border-border/70 px-4 py-8 text-center">
                    <CreditCard className="mx-auto h-5 w-5 text-muted-foreground/50" />
                    <div className="mt-2 text-[12.5px] text-muted-foreground">
                      No payments initiated yet.
                    </div>
                    {canTakePayment && (
                      <div className="mt-3">
                        <Button size="sm" onClick={() => setTakePaymentOpen(true)}>
                          <CreditCard className="h-3.5 w-3.5" />
                          Take payment
                        </Button>
                      </div>
                    )}
                  </div>
                ) : payments.length > 0 ? (
                  <div className="overflow-hidden rounded-lg border border-border/70">
                    <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      <div className="col-span-3">Number</div>
                      <div className="col-span-2">Provider</div>
                      <div className="col-span-3 text-right">Amount</div>
                      <div className="col-span-2">Status</div>
                      <div className="col-span-2 text-right">Initiated</div>
                    </div>
                    {payments.map((p) => (
                      <PaymentRow key={p.id} payment={p} />
                    ))}
                  </div>
                ) : null}
              </CollapsibleSection>

              {/* ─── Invoices (collapsible) ────────────────────────── */}
              <CollapsibleSection
                title="Invoices"
                count={invoices.length}
                defaultOpen={invoices.length > 0}
                action={
                  canGenerateInvoice ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setGenerateInvoiceOpen(true)}
                    >
                      <ReceiptText className="h-3.5 w-3.5" />
                      Generate invoice
                    </Button>
                  ) : null
                }
              >
                {invoices.length === 0 && !invoicesQuery.isLoading ? (
                  <div className="rounded-lg border border-dashed border-border/70 px-4 py-8 text-center">
                    <ReceiptText className="mx-auto h-5 w-5 text-muted-foreground/50" />
                    <div className="mt-2 text-[12.5px] text-muted-foreground">
                      No invoice issued for this order yet.
                    </div>
                    {canGenerateInvoice && (
                      <div className="mt-3">
                        <Button size="sm" onClick={() => setGenerateInvoiceOpen(true)}>
                          <ReceiptText className="h-3.5 w-3.5" />
                          Generate invoice
                        </Button>
                      </div>
                    )}
                  </div>
                ) : invoices.length > 0 ? (
                  <div className="overflow-hidden rounded-lg border border-border/70">
                    <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      <div className="col-span-4">Number</div>
                      <div className="col-span-2">Date</div>
                      <div className="col-span-2">Tax</div>
                      <div className="col-span-2 text-right">Grand total</div>
                      <div className="col-span-1">Status</div>
                      <div className="col-span-1" />
                    </div>
                    {invoices.map((inv) => (
                      <InvoiceRow
                        key={inv.id}
                        invoice={inv}
                        onPrint={() => printInvoice.mutate(inv.id)}
                        onCancel={() => {
                          setCancelReason('');
                          setCancellingInvoice(inv);
                        }}
                      />
                    ))}
                  </div>
                ) : null}
              </CollapsibleSection>

              {/* ─── Aftersales (cancels + returns) ────────────────── */}
              {(cancelsForOrder.length > 0 || returnsForOrder.length > 0) && (
                <CollapsibleSection
                  title="Aftersales"
                  count={cancelsForOrder.length + returnsForOrder.length}
                  defaultOpen
                >
                  <div className="overflow-hidden rounded-lg border border-border/70">
                    {cancelsForOrder.map((c) => (
                      <div
                        key={`cancel-${c.id}`}
                        className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
                      >
                        <div className="col-span-1">
                          <Badge variant="warning" className="text-[9.5px]">Cancel</Badge>
                        </div>
                        <div className="col-span-3 truncate font-mono text-[12px] text-foreground">
                          {c.cancelNumber}
                        </div>
                        <div className="col-span-3 text-right font-mono tabular-nums">
                          {c.refundAmount != null
                            ? fmt.money(c.refundAmount, c.refundCurrency ?? c.salesOrder.currency)
                            : '—'}
                        </div>
                        <div className="col-span-3">
                          <Badge variant="muted" className="text-[10px]">{c.status}</Badge>
                        </div>
                        <div className="col-span-2 text-right text-[11.5px] text-muted-foreground">
                          {fmt.relative(c.createdAt)}
                        </div>
                      </div>
                    ))}
                    {returnsForOrder.map((r) => (
                      <div
                        key={`return-${r.id}`}
                        className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
                      >
                        <div className="col-span-1">
                          <Badge variant="info" className="text-[9.5px]">Return</Badge>
                        </div>
                        <div className="col-span-3 truncate font-mono text-[12px] text-foreground">
                          {r.returnNumber}
                        </div>
                        <div className="col-span-3 text-right text-[11.5px] text-muted-foreground">
                          {r.items.length} line{r.items.length === 1 ? '' : 's'}
                        </div>
                        <div className="col-span-3">
                          <Badge variant="muted" className="text-[10px]">{r.status}</Badge>
                        </div>
                        <div className="col-span-2 text-right text-[11.5px] text-muted-foreground">
                          {fmt.relative(r.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* ─── Refunds (collapsible, only if any) ────────────── */}
              {refunds.length > 0 && (
                <CollapsibleSection title="Refunds" count={refunds.length} defaultOpen>
                  <div className="overflow-hidden rounded-lg border border-border/70">
                    {refunds.map((r) => (
                      <div
                        key={r.id}
                        className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0"
                      >
                        <div className="col-span-4 truncate font-mono text-[12px] text-foreground">
                          {r.refundNumber}
                        </div>
                        <div className="col-span-3 text-right font-mono tabular-nums">
                          {fmt.money(Number(r.amount), r.currency)}
                        </div>
                        <div className="col-span-3">
                          <Badge variant="muted" className="text-[10px]">
                            {r.status}
                          </Badge>
                        </div>
                        <div className="col-span-2 text-right text-[11.5px] text-muted-foreground">
                          {fmt.relative(r.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}

              {/* ─── Status timeline (collapsible, bottom) ───────────── */}
              <CollapsibleSection
                title="Status timeline"
                count={current?.statusHistory?.length ?? 0}
              >
                <OrderStatusTimeline history={current?.statusHistory ?? []} />
              </CollapsibleSection>
            </div>
          </ScrollArea>

          {/* Footer — operational mutations */}
          <div className="flex items-center justify-between gap-2 border-t border-border/60 bg-muted/30 px-5 py-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" disabled>
                  <FileText className="h-3.5 w-3.5" />
                  Print invoice
                </Button>
              </TooltipTrigger>
              <TooltipContent>Lands with the payment slice</TooltipContent>
            </Tooltip>

            <div className="flex items-center gap-2">
              {!isTerminal && canMarkReady && (
                <Button size="sm" onClick={() => update('READY_FOR_DISPATCH')} disabled={mutation.isPending}>
                  <Check className="h-3.5 w-3.5" />
                  {pendingStatus === 'READY_FOR_DISPATCH' ? 'Marking…' : 'Mark ready for dispatch'}
                </Button>
              )}
              {!isTerminal && canMarkDispatched && !forwardDispatch && (
                <Button size="sm" onClick={() => update('DISPATCHED')} disabled={mutation.isPending}>
                  <Truck className="h-3.5 w-3.5" />
                  {pendingStatus === 'DISPATCHED' ? 'Updating…' : 'Mark dispatched'}
                </Button>
              )}
              {!isTerminal && canMarkDispatched && forwardDispatch && (
                <span className="text-[12px] text-muted-foreground">
                  Dispatch {forwardDispatch.dispatchNumber} in progress
                </span>
              )}
              {!isTerminal && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={mutation.isPending}>
                      <MoreHorizontal className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {status === 'CREATED' && (
                      <DropdownMenuItem onClick={() => update('PAYMENT_PENDING')}>
                        Move to payment pending
                      </DropdownMenuItem>
                    )}
                    {status === 'PAYMENT_PENDING' && (
                      <DropdownMenuItem onClick={() => update('PAYMENT_CONFIRMED')}>
                        Mark payment confirmed
                      </DropdownMenuItem>
                    )}
                    {status === 'DISPATCHED' && !forwardDispatch && (
                      <DropdownMenuItem onClick={() => update('DELIVERED')}>
                        <Check className="h-3.5 w-3.5" />
                        Mark delivered
                      </DropdownMenuItem>
                    )}
                    {canCancel && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => update('CANCELLED')}
                          className="text-destructive focus:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                          Cancel order
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {isTerminal && (
                <span className="text-[12px] text-muted-foreground">
                  {status === 'DELIVERED'
                    ? 'Order delivered'
                    : status === 'CANCELLED'
                    ? 'Order cancelled'
                    : 'Order dispatched'}
                </span>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PaymentInitiateDialog
        order={current}
        open={takePaymentOpen}
        onOpenChange={setTakePaymentOpen}
      />

      <RefundInitiateDialog
        order={current}
        open={issueRefundOpen}
        onOpenChange={setIssueRefundOpen}
      />

      <GenerateInvoiceDialog
        order={current}
        open={generateInvoiceOpen}
        onOpenChange={setGenerateInvoiceOpen}
      />

      {/* Cancel invoice + (optional) regenerate. Captures a reason so
          the audit trail explains why the issued invoice was reversed
          (typical: wrong line, wrong address, wrong tax classification). */}
      <Dialog
        open={!!cancellingInvoice}
        onOpenChange={(o) => !o && setCancellingInvoice(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Cancel invoice {cancellingInvoice?.invoiceNumber}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 px-1 pb-1 text-[13px] leading-relaxed">
            <p className="text-muted-foreground">
              Cancelling marks the invoice as CANCELLED. The audit log keeps
              the original record; the Generate-invoice CTA reappears so you
              can issue a fresh one in its place.
            </p>
            <p className="text-muted-foreground">
              A new dialog opens automatically after cancellation so you can
              issue the replacement without hunting for the button.
            </p>
            <div>
              <label
                htmlFor="cancelReason"
                className="mb-1 block text-[11.5px] font-medium text-foreground"
              >
                Reason (optional, audit trail)
              </label>
              <Textarea
                id="cancelReason"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Wrong line items / wrong customer address / GSTIN incorrect"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCancellingInvoice(null)}
              disabled={cancelInvoice.isPending}
            >
              Keep invoice
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() =>
                cancellingInvoice &&
                cancelInvoice.mutate({
                  id: cancellingInvoice.id,
                  reason: cancelReason.trim() || undefined,
                })
              }
              disabled={cancelInvoice.isPending}
            >
              <Ban className="h-3.5 w-3.5" />
              {cancelInvoice.isPending ? 'Cancelling…' : 'Cancel + regenerate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

const INVOICE_STATUS_TONE: Record<InvoiceStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'muted',
  ISSUED: 'warning',
  CANCELLED: 'destructive',
  PAID: 'success',
  PARTIALLY_PAID: 'success',
};

function InvoiceRow({
  invoice,
  onPrint,
  onCancel,
}: {
  invoice: Invoice;
  onPrint: () => void;
  onCancel: () => void;
}) {
  const intra = Number(invoice.totalIgst) === 0;
  // Cancel is only meaningful for live invoices. PAID / PARTIALLY_PAID
  // would need a payment-side reversal first; CANCELLED is a no-op.
  const canCancel = invoice.status === 'ISSUED';
  return (
    <div className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0">
      <div className="col-span-4 min-w-0">
        <div className="truncate font-mono text-[12px] text-foreground">
          {invoice.invoiceNumber}
        </div>
        <div className="truncate text-[10.5px] text-muted-foreground">
          PoS {invoice.placeOfSupply} →{' '}
          {invoice.billingSnapshot.state ?? '—'}
        </div>
      </div>
      <div className="col-span-2 text-[12px] text-muted-foreground">
        {fmt.date(invoice.invoiceDate)}
      </div>
      <div className="col-span-2">
        <Badge variant="muted" className="text-[10px]">
          {intra ? 'CGST+SGST' : 'IGST'}
        </Badge>
      </div>
      <div className="col-span-2 text-right font-mono text-[12.5px] tabular-nums text-foreground">
        {fmt.money(Number(invoice.grandTotal), invoice.currency)}
      </div>
      <div className="col-span-1">
        <Badge variant={INVOICE_STATUS_TONE[invoice.status]} className="text-[10px]">
          {invoice.status === 'PARTIALLY_PAID' ? 'Partial' : invoice.status[0] + invoice.status.slice(1).toLowerCase()}
        </Badge>
      </div>
      <div className="col-span-1 flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              aria-label="Invoice actions"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onPrint}>
              <Printer className="h-3.5 w-3.5" />
              Print invoice
            </DropdownMenuItem>
            {canCancel && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onCancel}
                  className="text-destructive focus:text-destructive"
                >
                  <Ban className="h-3.5 w-3.5" />
                  Cancel + regenerate
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

const PAYMENT_STATUS_TONE: Record<PaymentStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  PENDING: 'muted',
  INITIATED: 'warning',
  SUCCESS: 'success',
  FAILED: 'destructive',
  CANCELLED: 'muted',
  REFUND_PENDING: 'warning',
  REFUNDED: 'muted',
};

function PaymentRow({ payment }: { payment: Payment }) {
  return (
    <div className="grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 text-[12.5px] last:border-0">
      <div className="col-span-3 min-w-0">
        <div className="truncate font-mono text-[12px] text-foreground">{payment.paymentNumber}</div>
        {payment.externalPaymentRef && (
          <div className="truncate font-mono text-[10.5px] text-muted-foreground">
            {payment.externalPaymentRef}
          </div>
        )}
      </div>
      <div className="col-span-2 font-mono text-[11.5px] text-muted-foreground">
        {payment.provider}
      </div>
      <div className="col-span-3 text-right font-mono tabular-nums">
        {fmt.money(Number(payment.amount), payment.currency)}
      </div>
      <div className="col-span-2">
        <Badge variant={PAYMENT_STATUS_TONE[payment.status]} className="text-[10px]">
          {payment.status}
        </Badge>
      </div>
      <div className="col-span-2 text-right text-[11.5px] text-muted-foreground">
        {payment.initiatedAt ? fmt.relative(payment.initiatedAt) : fmt.relative(payment.createdAt)}
      </div>
    </div>
  );
}

const DISPATCH_STATUS_LABEL: Record<DispatchStatus, string> = {
  PENDING: 'Pending pack',
  READY_FOR_PICKUP: 'Packed — awaiting pickup',
  PICKED: 'Picked',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  FAILED: 'Failed',
  RETURN_INITIATED: 'Return initiated',
  RETURNED: 'Returned',
  CANCELLED: 'Cancelled',
};

const DISPATCH_STATUS_TONE: Record<DispatchStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  PENDING: 'muted',
  READY_FOR_PICKUP: 'warning',
  PICKED: 'warning',
  IN_TRANSIT: 'success',
  OUT_FOR_DELIVERY: 'success',
  DELIVERED: 'success',
  FAILED: 'destructive',
  RETURN_INITIATED: 'warning',
  RETURNED: 'muted',
  CANCELLED: 'muted',
};

function DispatchRow({ dispatch }: { dispatch: DispatchOrder }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-lg border border-border/70 px-4 py-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-[12px] font-medium text-foreground">
          {dispatch.dispatchNumber}
        </span>
        <Badge
          variant={dispatch.direction === 'REVERSE' ? 'warning' : 'muted'}
          className="text-[10px]"
        >
          {dispatch.direction === 'REVERSE' ? 'Return pickup' : 'Forward'}
        </Badge>
        <Badge variant={DISPATCH_STATUS_TONE[dispatch.status]} className="text-[10px]">
          {DISPATCH_STATUS_LABEL[dispatch.status]}
        </Badge>
        <button
          type="button"
          onClick={() => navigate(`/dispatch?id=${dispatch.id}`)}
          className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-medium text-primary hover:underline"
        >
          Open dispatch
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground sm:grid-cols-3">
        <div>
          <span className="text-muted-foreground/70">Partner</span>
          <div className="text-foreground">{dispatch.partner?.name ?? 'Unassigned'}</div>
        </div>
        <div>
          <span className="text-muted-foreground/70">AWB / Tracking</span>
          <div className="font-mono text-foreground">{dispatch.trackingNumber ?? '—'}</div>
        </div>
        <div>
          <span className="text-muted-foreground/70">Created</span>
          <div className="text-foreground">{fmt.dateTime(dispatch.createdAt)}</div>
        </div>
        <div>
          <span className="text-muted-foreground/70">Courier pickup</span>
          <div className="text-foreground">
            {dispatch.pickupScheduledDate ?? (dispatch.pickupScheduledAt ? 'Requested' : '—')}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShippingAddressBlock({
  address,
}: {
  address: NonNullable<SalesOrder['customer']['shippingAddress']>;
}) {
  const cityLine = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');
  return (
    <div className="rounded-lg border border-border/70 bg-muted/20 px-4 py-3 text-[12.5px] leading-relaxed">
      <div className="flex items-start gap-2">
        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1 space-y-0.5">
          {address.contactName && (
            <div className="font-medium text-foreground">{address.contactName}</div>
          )}
          <div className="text-foreground">{address.line1}</div>
          {address.line2 && <div className="text-foreground">{address.line2}</div>}
          {cityLine && <div className="text-foreground">{cityLine}</div>}
          <div className="font-mono text-[11px] uppercase text-muted-foreground">
            {address.country}
          </div>
          {(address.contactPhone || address.gstin) && (
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 pt-1 text-[11.5px] text-muted-foreground">
              {address.contactPhone && (
                <span className="inline-flex items-center gap-1 font-mono">
                  <Phone className="h-3 w-3" />
                  {address.contactPhone}
                </span>
              )}
              {address.gstin && (
                <span className="inline-flex items-center gap-1 font-mono">
                  GSTIN {address.gstin}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between',
        bold ? 'text-[15px] font-semibold' : 'text-[12.5px]',
      )}
    >
      <span className={bold ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
      <span
        className={cn(
          'font-mono tabular-nums',
          muted && !bold ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function CollapsibleSection({
  title,
  count,
  defaultOpen = false,
  action,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="group flex items-center gap-2 text-left"
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition group-hover:text-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition group-hover:text-foreground" />
          )}
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground">
            {title}
          </span>
          {typeof count === 'number' && count > 0 && (
            <span className="rounded bg-muted/60 px-1 font-mono text-[10px] tabular-nums text-muted-foreground">
              {count}
            </span>
          )}
        </button>
        {action}
      </div>
      {open && <div>{children}</div>}
    </section>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[110px] px-4 py-3.5 first:pl-0 last:pr-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 font-mono text-[19px] leading-none tabular-nums',
          accent ? 'font-semibold text-foreground' : muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

// ── OrderStatusTimeline ───────────────────────────────────────────
//
// Renders the sales-order status transition log oldest-first. Mirrors
// the dispatch-detail timeline visually so the operator gets the same
// chronological cue on both surfaces. Carrier-side dispatch transitions
// are mirrored onto the SO by OrdersService.transitionStatusInTx, so
// one feed covers operator + automated movements.

const ACTOR_LABEL: Record<string, string> = {
  USER: 'Operator',
  API_CLIENT: 'API',
  SYSTEM: 'System',
  PORTAL: 'Portal',
  WEBHOOK: 'Webhook',
};

function OrderStatusTimeline({
  history,
}: {
  history: import('@/types/orders').SalesOrderStatusEvent[];
}) {
  if (history.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/70 px-4 py-8 text-center text-[12.5px] text-muted-foreground">
        No status changes recorded yet.
      </div>
    );
  }
  return (
    <ol className="pl-0.5">
      {history.map((h, i) => {
        const last = i === history.length - 1;
        return (
          <li key={`${h.toStatus}-${h.createdAt}-${i}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'mt-1 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-background',
                  last ? 'bg-primary' : 'bg-border',
                )}
              />
              {!last && <span className="w-px flex-1 bg-border" />}
            </div>
            <div className={cn('min-w-0', last ? 'pb-1' : 'pb-4')}>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-[12.5px] font-semibold text-foreground">
                  {STATUS_LABEL[h.toStatus] ?? h.toStatus}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {fmt.dateTime(h.createdAt)}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                {h.reason && <span>{h.reason}</span>}
                {h.actorType && (
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wide">
                    {ACTOR_LABEL[h.actorType] ?? h.actorType}
                  </span>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
